import { useApi } from '@omnipresentgroup/api';
import type {
  ContractorInvoiceAPI,
  GetContractorInvoicesResponseAPI,
} from '@omnipresentgroup/types';
import { useQueries, useQuery } from '@tanstack/react-query';

import { API_BASE, API_PATH } from '../constants';
import {
  transformContractorInvoiceFromAPI,
  transformGetContractorInvoicesResponseFromAPI,
} from '../mappers';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_INDEX = 0;

type QueryParameters = {
  page?: number;
  size?: number;
  contractorId?: string;
  userId?: string;
  clientId?: string;
  companyId?: string;
};

export const useGetContractorsInvoices = ({
  page,
  size,
  contractorId,
  userId,
  clientId,
  companyId,
}: QueryParameters) => {
  const { GET } = useApi();
  const query = useQuery({
    queryKey: [API_PATH, page, size, contractorId, userId, clientId, companyId],
    queryFn: () =>
      GET<GetContractorInvoicesResponseAPI>(
        `${API_BASE}/${API_PATH}`,
        {},
        {
          ...(page && { page: String(page) }),
          ...(size && { size: String(size) }),
          ...(contractorId && { contractor_id: contractorId }),
          ...(userId && { user_id: userId }),
          ...(clientId && { client_id: clientId }),
          ...(companyId && { company_id: companyId }),
        },
      ),
    select: (data) => ({
      ...data,
      items: transformGetContractorInvoicesResponseFromAPI(data).items,
    }),
  });

  return query;
};

export const useGetContractorInvoicesByIds = ({
  ids = [],
  enabled,
}: {
  ids: string[];
  enabled?: boolean;
}) => {
  const { GET } = useApi();

  const query = useQueries({
    queries: ids.map((id) => ({
      queryKey: [API_PATH, id],
      queryFn: id
        ? () => GET<ContractorInvoiceAPI>(`${API_BASE}/${API_PATH}/${id}`)
        : () => Promise.reject(new Error('Invalid id to fetch an invoice')),
      enabled: !!id || enabled,
      select: transformContractorInvoiceFromAPI,
    })),
  });

  return query;
};
